import { useState, type CSSProperties, type ReactNode } from "react";
import { GovernanceHealthView } from "./GovernanceHealthView.tsx";
import { ReviewTab } from "./ReviewTab.tsx";
import { ResultsTab } from "./ResultsTab.tsx";
import { EvaluationsTab } from "./EvaluationsTab.tsx";
import { useQuery } from "@tanstack/react-query";
import {
  engagementRfpHtmlUrl,
  evaluationDownloadUrl,
  evaluationReportHtmlUrl,
  evaluationRfpHtmlUrl,
  fetchClients,
  fetchEngagementDetail,
  fetchEngagements,
  fetchEvaluationDetail,
  fetchEvaluations,
  type ClientSummary,
  type EngagementSummary,
  type EvaluationSummary,
  type RankedProposal,
} from "../api/lge.ts";

export interface TabIdentity {
  role: string;
  client_id: string | null;
  display_name: string;
}

// Governance tab — increment 1 (read path). Client roster → engagement
// (RFP) list/detail with submission links → evaluation viewer. Read-only:
// generate-rfp, HITL review, and the market-health/monitor views land in
// increments 2/3.
//
// Palette matches the analytics cards (hardcoded, like QuoteQualityCard).
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const RED = "#e07070";
const ACCENT = "#6699ff";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

const GROUPS: { key: ClientSummary["category"]; label: string; note: string }[] = [
  { key: "active", label: "Active mandates", note: "venue bindings in registry" },
];

const card: CSSProperties = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "14px 16px",
};

const chip = (color: string): CSSProperties => ({
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 5,
  color,
  border: `1px solid ${color}55`,
  whiteSpace: "nowrap",
});

const linkBtn: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 5,
  border: `1px solid rgba(255,255,255,0.14)`,
  background: "transparent",
  color: TEXT,
  cursor: "pointer",
  textDecoration: "none",
};

function fmtTs(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
}

// Engagement venues may be composite ("kraken|ALEO/USD") when a venue splits
// by pair — render as "venue · pair".
function venueLabel(v: string): string {
  return v.includes("|") ? v.split("|").join(" · ") : v;
}

const STATUS_COLOR: Record<string, string> = {
  RFP_ISSUED: ACCENT,
  TOKENS_CREATED: ACCENT,
  SCORED: GREEN,
  SELECTED: GREEN,
  MONITORING: GREEN,
  RFP_PENDING: AMBER,
  UNKNOWN: MUTED,
  ERROR: RED,
};

function statusChip(status: string): ReactNode {
  const color = STATUS_COLOR[status] ?? MUTED;
  return <span style={chip(color)}>{status.replaceAll("_", " ")}</span>;
}

function States({
  isLoading,
  isError,
  error,
  emptyText,
  isEmpty,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  emptyText: string;
  isEmpty: boolean;
}): ReactNode {
  if (isLoading)
    return <div style={{ color: MUTED, padding: 24, fontSize: 13 }}>Loading…</div>;
  if (isError)
    return (
      <div style={{ color: RED, padding: 24, fontSize: 13 }}>
        {error instanceof Error ? error.message : "Request failed"}
      </div>
    );
  if (isEmpty)
    return <div style={{ color: MUTED, padding: 24, fontSize: 13 }}>{emptyText}</div>;
  return null;
}

// --- Navigation (local state; the app has no router) --------------------------

type View =
  | { kind: "home" }
  | { kind: "client"; clientId: string; displayName: string }
  | { kind: "engagement"; clientId: string; displayName: string; rfpId: string }
  | { kind: "evaluation"; rfpId: string };

export function GovernanceTab({ identity }: { identity: TabIdentity }) {
  const isClient = identity.role !== "operator";
  const [view, setView] = useState<View>(
    isClient && identity.client_id
      ? {
          kind: "client",
          clientId: identity.client_id,
          displayName: identity.display_name,
        }
      : { kind: "home" },
  );
  const [sub, setSub] = useState<"health" | "rfp" | "review" | "results" | "evaluations">("health");
  const [resultsRfp, setResultsRfp] = useState<string | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="subtabs" style={{ display: "flex", gap: 8 }}>
        {([["health", "Health"], ["rfp", "Engagements"], ["review", "Review"],
          ["results", "Results"], ["evaluations", "Evaluations"]] as const).map(
          ([id, label]) => (
            <button key={id} onClick={() => {
                if (id === "results") setResultsRfp(null);
                setSub(id);
              }}
              style={{ fontSize: 13, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${sub === id ? "rgba(102,153,255,0.35)" : "transparent"}`,
                background: sub === id ? "rgba(102,153,255,0.16)" : "transparent",
                color: sub === id ? TEXT : MUTED }}>
              {label}
            </button>
          ),
        )}
      </div>
      {sub === "health" && <GovernanceHealthView identity={identity} />}
      {sub === "review" && (
        <ReviewTab onOpenResults={(r) => { setResultsRfp(r); setSub("results"); }} />
      )}
      {sub === "results" && <ResultsTab initialRfp={resultsRfp} />}
      {sub === "evaluations" && <EvaluationsTab />}
      {sub === "rfp" && (<>
      <Breadcrumb view={view} setView={setView} allowHome={!isClient} />
      {view.kind === "home" && <Home setView={setView} />}
      {view.kind === "client" && (
        <ClientEngagements
          clientId={view.clientId}
          displayName={view.displayName}
          setView={setView}
        />
      )}
      {view.kind === "engagement" && (
        <EngagementView clientId={view.clientId} rfpId={view.rfpId} />
      )}
      {view.kind === "evaluation" && <EvaluationView rfpId={view.rfpId} />}
      </>)}
    </div>
  );
}

function Breadcrumb({
  view,
  setView,
  allowHome,
}: {
  view: View;
  setView: (v: View) => void;
  allowHome: boolean;
}) {
  if (view.kind === "home") return null;
  const crumbs: { label: string; to?: View }[] = [
    { label: "Governance", to: allowHome ? { kind: "home" } : undefined },
  ];
  if (view.kind === "client")
    crumbs.push({ label: view.displayName });
  if (view.kind === "engagement") {
    crumbs.push({
      label: view.displayName,
      to: { kind: "client", clientId: view.clientId, displayName: view.displayName },
    });
    crumbs.push({ label: view.rfpId });
  }
  if (view.kind === "evaluation") crumbs.push({ label: `Evaluation · ${view.rfpId}` });
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
      {crumbs.map((c, i) => (
        <span key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {i > 0 && <span style={{ color: MUTED }}>/</span>}
          {c.to ? (
            <button
              style={{ ...linkBtn, border: "none", padding: 0, color: ACCENT }}
              onClick={() => setView(c.to!)}
            >
              {c.label}
            </button>
          ) : (
            <span style={{ color: TEXT }}>{c.label}</span>
          )}
        </span>
      ))}
    </div>
  );
}

// --- Home: client roster + evaluations ----------------------------------------

function Home({ setView }: { setView: (v: View) => void }) {
  return (
    <>
      <ClientGrid setView={setView} />
      <EvaluationList setView={setView} />
    </>
  );
}

function ClientGrid({ setView }: { setView: (v: View) => void }) {
  const q = useQuery({ queryKey: ["lge", "clients"], queryFn: fetchClients });
  const clients = q.data?.clients ?? [];
  return (
    <div className="panel">
      <div className="panel-head">
        <h2 style={{ margin: 0 }}>Clients</h2>
        <div className="panel-meta" style={{ color: MUTED, fontSize: 12 }}>
          registry: config/users.json · read-only
        </div>
      </div>
      <States
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={!q.isLoading && !q.isError && clients.length === 0}
        emptyText="No clients in registry."
      />
      {GROUPS.map(({ key, label, note }) => {
        const group = clients.filter((c) => c.category === key);
        if (group.length === 0) return null;
        return (
          <div key={key} style={{ marginBottom: 6 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                margin: "12px 0 8px",
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: TEXT,
                }}
              >
                {label}
              </span>
              <span style={{ fontSize: 11, color: MUTED }}>{note}</span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {group.map((c) => (
                <ClientCard key={c.client_id} c={c} setView={setView} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ClientCard({ c, setView }: { c: ClientSummary; setView: (v: View) => void }) {
  return (
    <div
      style={{ ...card, cursor: "pointer" }}
      onClick={() =>
        setView({ kind: "client", clientId: c.client_id, displayName: c.display_name })
      }
    >
      <div
        style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}
      >
        <span style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: 4,
              marginRight: 8,
              background: c.has_legacy_data ? GREEN : MUTED,
            }}
            title={c.has_legacy_data ? "legacy data present" : "no legacy data dir"}
          />
          {c.display_name}
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>
          {c.engagement_count} engagement{c.engagement_count === 1 ? "" : "s"}
        </span>
      </div>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>{c.primary_pair}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {c.cex_venues.map((v) => (
          <span key={v} style={chip(ACCENT)}>
            {v}
          </span>
        ))}
        {c.dex_venues.map((v) => (
          <span key={v} style={chip(AMBER)}>
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

// --- Client → engagement list --------------------------------------------------

function ClientEngagements({
  clientId,
  displayName,
  setView,
}: {
  clientId: string;
  displayName: string;
  setView: (v: View) => void;
}) {
  const q = useQuery({
    queryKey: ["lge", "engagements", clientId],
    queryFn: () => fetchEngagements(clientId),
  });
  const engs = q.data?.engagements ?? [];
  return (
    <div className="panel">
      <div className="panel-head">
        <h2 style={{ margin: 0 }}>{displayName} · Engagements</h2>
      </div>
      <States
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={!q.isLoading && !q.isError && engs.length === 0}
        emptyText="No engagements yet. RFP generation lands in increment 2; use the legacy portal to initiate."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {engs.map((e) => (
          <EngagementRow
            key={e.rfp_id}
            e={e}
            onOpen={() =>
              setView({ kind: "engagement", clientId, displayName, rfpId: e.rfp_id })
            }
          />
        ))}
      </div>
    </div>
  );
}

function EngagementRow({ e, onOpen }: { e: EngagementSummary; onOpen: () => void }) {
  return (
    <div style={{ ...card, cursor: "pointer" }} onClick={onOpen}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: ACCENT }}>{e.rfp_id}</span>
        <span style={{ fontSize: 12, color: MUTED }}>{fmtTs(e.generated_at)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: 12,
          color: MUTED,
        }}
      >
        {statusChip(e.status)}
        <span>
          pack: <span style={{ color: TEXT }}>{e.pack_id || "—"}</span>
        </span>
        <span>
          venues:{" "}
          <span style={{ color: TEXT }}>
            {e.venues.length ? e.venues.map(venueLabel).join(", ") : "—"}
          </span>
        </span>
        {e.winner && (
          <span>
            winner: <span style={{ color: GREEN }}>{e.winner}</span>
          </span>
        )}
      </div>
    </div>
  );
}

// --- Engagement detail ----------------------------------------------------------

function EngagementView({ clientId, rfpId }: { clientId: string; rfpId: string }) {
  const q = useQuery({
    queryKey: ["lge", "engagement", clientId, rfpId],
    queryFn: () => fetchEngagementDetail(clientId, rfpId),
  });
  const d = q.data;
  return (
    <div className="panel">
      <States
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={false}
        emptyText=""
      />
      {d && (
        <>
          <div className="panel-head" style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>{d.rfp_id}</h2>
            <div
              className="panel-meta"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              {statusChip(d.status)}
              <span style={{ fontSize: 12, color: MUTED }}>{fmtTs(d.generated_at)}</span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              fontSize: 13,
              color: MUTED,
              marginBottom: 14,
            }}
          >
            <span>
              pack: <span style={{ color: TEXT }}>{d.pack_id || "—"}</span>
            </span>
            <span>
              term:{" "}
              <span style={{ color: TEXT }}>
                {d.engagement_term_days != null ? `${d.engagement_term_days}d` : "—"}
              </span>
            </span>
            <span>
              max submissions:{" "}
              <span style={{ color: TEXT }}>{d.max_submissions ?? "—"}</span>
            </span>
            {d.selection?.entity_name && (
              <span>
                winner: <span style={{ color: GREEN }}>{d.selection.entity_name}</span>
              </span>
            )}
            {d.has_rfp_html && (
              <a
                href={engagementRfpHtmlUrl(clientId, rfpId)}
                target="_blank"
                rel="noreferrer"
                style={{ ...linkBtn, color: ACCENT }}
              >
                View RFP document ↗
              </a>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
            {d.venues.map((v) => (
              <span key={v} style={chip(ACCENT)}>
                {venueLabel(v)}
              </span>
            ))}
          </div>

          <div style={card}>
            <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
              Submission links — operator-only; each link is MM-unique and
              deadline-bound.
            </div>
            {d.submission_tokens.length === 0 && (
              <div style={{ fontSize: 13, color: MUTED }}>No tokens generated.</div>
            )}
            {d.submission_tokens.map((t) => (
              <TokenRow
                key={t.token}
                mm={t.market_maker}
                url={t.submission_url}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TokenRow({ mm, url }: { mm: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 0",
        borderBottom: `1px solid ${BORDER}`,
        fontSize: 13,
      }}
    >
      <span style={{ minWidth: 120, fontWeight: 600, color: TEXT }}>
        {mm && mm !== "default" ? mm : "—"}
      </span>
      <span
        style={{
          flex: 1,
          color: ACCENT,
          fontFamily: "SF Mono, Consolas, monospace",
          fontSize: 12,
          wordBreak: "break-all",
        }}
      >
        {url}
      </span>
      <button
        style={linkBtn}
        onClick={() => {
          navigator.clipboard
            .writeText(url)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => {});
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// --- Evaluations -----------------------------------------------------------------

function EvaluationList({ setView }: { setView: (v: View) => void }) {
  const q = useQuery({ queryKey: ["lge", "evaluations"], queryFn: fetchEvaluations });
  const evs = q.data?.evaluations ?? [];
  return (
    <div className="panel">
      <div className="panel-head">
        <h2 style={{ margin: 0 }}>Evaluations</h2>
        <div className="panel-meta" style={{ color: MUTED, fontSize: 12 }}>
          artifacts are read-only and hash-verified
        </div>
      </div>
      <States
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={!q.isLoading && !q.isError && evs.length === 0}
        emptyText="No evaluation artifacts."
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {evs.map((e) => (
          <EvaluationRow
            key={e.rfp_id}
            e={e}
            onOpen={() => setView({ kind: "evaluation", rfpId: e.rfp_id })}
          />
        ))}
      </div>
    </div>
  );
}

function EvaluationRow({ e, onOpen }: { e: EvaluationSummary; onOpen: () => void }) {
  const stable = e.stability_label === "STABLE";
  return (
    <div style={{ ...card, cursor: "pointer" }} onClick={onOpen}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: ACCENT }}>
          {e.token} · {e.rfp_id}
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>{fmtTs(e.generated_at)}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "center",
          flexWrap: "wrap",
          fontSize: 12,
          color: MUTED,
        }}
      >
        <span style={chip(stable ? GREEN : AMBER)}>{e.stability_label}</span>
        <span>
          {e.total_proposals} proposal{e.total_proposals === 1 ? "" : "s"} ·{" "}
          <span style={{ color: GREEN }}>{e.passed} passed</span> ·{" "}
          <span style={{ color: RED }}>{e.failed} failed</span>
        </span>
        {e.top_proposal && (
          <span>
            leader: <span style={{ color: TEXT }}>{e.top_proposal}</span>{" "}
            <span style={{ fontFamily: "monospace" }}>{e.top_score.toFixed(1)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function EvaluationView({ rfpId }: { rfpId: string }) {
  const q = useQuery({
    queryKey: ["lge", "evaluation", rfpId],
    queryFn: () => fetchEvaluationDetail(rfpId),
  });
  const d = q.data;
  const memoOrder = [
    "executive_summary",
    "recommendation",
    "stability_assessment",
    "hard_fail_summary",
    "flags_summary",
  ];
  return (
    <div className="panel">
      <States
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={false}
        emptyText=""
      />
      {d && (
        <>
          <div className="panel-head" style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>
              {d.token} · {d.rfp_id}
            </h2>
            <div
              className="panel-meta"
              style={{ display: "flex", gap: 10, alignItems: "center" }}
            >
              <span style={chip(d.stability_label === "STABLE" ? GREEN : AMBER)}>
                {d.stability_label}
              </span>
              <span style={{ fontSize: 12, color: MUTED }}>{fmtTs(d.generated_at)}</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            {d.has_report_html && (
              <a
                href={evaluationReportHtmlUrl(rfpId)}
                target="_blank"
                rel="noreferrer"
                style={{ ...linkBtn, color: ACCENT }}
              >
                Scoring report ↗
              </a>
            )}
            {d.has_rfp_html && (
              <a
                href={evaluationRfpHtmlUrl(rfpId)}
                target="_blank"
                rel="noreferrer"
                style={{ ...linkBtn, color: ACCENT }}
              >
                RFP document ↗
              </a>
            )}
            <a href={evaluationDownloadUrl(rfpId)} style={{ ...linkBtn, color: TEXT }}>
              Download JSON
            </a>
          </div>

          {Object.keys(d.board_memo.sections).length > 0 && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                Board memo
              </div>
              {memoOrder
                .filter((k) => d.board_memo.sections[k])
                .map((k) => (
                  <div key={k} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: ACCENT,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 2,
                      }}
                    >
                      {k.replaceAll("_", " ")}
                    </div>
                    <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55 }}>
                      {d.board_memo.sections[k]}
                    </div>
                  </div>
                ))}
            </div>
          )}

          <RankedTable ranked={d.ranked_list} summaries={d.proposal_summaries} />

          {d.board_memo.disclaimer && (
            <div style={{ fontSize: 11, color: MUTED, marginTop: 14, lineHeight: 1.5 }}>
              {d.board_memo.disclaimer}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RankedTable({
  ranked,
  summaries,
}: {
  ranked: RankedProposal[];
  summaries: { proposal_id: string; economic_terms: { compensation_model: string | null; monthly_retainer_usd: number | null } }[];
}) {
  const sm = new Map(summaries.map((s) => [s.proposal_id, s.economic_terms]));
  const fmtUsd = (v: number | null | undefined) =>
    v == null ? "—" : v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${v.toLocaleString()}`;
  const th: CSSProperties = {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 11,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    borderBottom: `1px solid ${BORDER}`,
  };
  const td: CSSProperties = {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: `1px solid ${BORDER}`,
    color: TEXT,
  };
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Market maker</th>
            <th style={th}>Rank</th>
            <th style={th}>Score [band]</th>
            <th style={th}>Structure</th>
            <th style={th}>Retainer</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((r) => {
            const econ = sm.get(r.proposal_id);
            return (
              <tr key={r.proposal_id || r.entity_name}>
                <td style={{ ...td, fontWeight: 600 }}>{r.entity_name}</td>
                <td style={td}>
                  {r.hard_fail ? (
                    <span style={{ color: RED, fontWeight: 700 }}>FAILED</span>
                  ) : (
                    <span style={{ color: GREEN, fontWeight: 700 }}>#{r.rank}</span>
                  )}
                </td>
                <td style={{ ...td, fontFamily: "monospace" }}>
                  {r.hard_fail ? (
                    <span style={{ color: MUTED }}>
                      0{" "}
                      {r.hypothetical_score != null &&
                        `(hyp: ${r.hypothetical_score.toFixed(1)})`}
                    </span>
                  ) : (
                    <>
                      <span style={{ color: ACCENT, fontWeight: 700 }}>
                        {r.final_score?.toFixed(1) ?? "—"}
                      </span>{" "}
                      {r.band_low != null && r.band_high != null && (
                        <span style={{ color: MUTED, fontSize: 11 }}>
                          [{r.band_low.toFixed(2)}–{r.band_high.toFixed(2)}]
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td style={td}>
                  {(econ?.compensation_model ?? "—").replaceAll("_", " ")}
                </td>
                <td style={td}>{fmtUsd(econ?.monthly_retainer_usd)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
